import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { compose } from "redux";
import { useForm } from "react-hook-form";
import { withDefaultLayout } from "@Hocs";
import { Container, StyledHeader } from "./ProfileSettings.styled";
import { IUserData } from "@Interfaces";
import { AccountTypes } from "@Interfaces/Entities/User";
import { AccountService } from "@Services";
import { ImageType } from "@Interfaces/Entities/Images";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { DialogComponents } from "@Interfaces/Entities/Dialogs";
import {
    AiUsageActions,
    AuthActions,
    NotificationActions,
    RootStateType,
} from "@Store";
import UserAvatar from "@Components/PollItem/UserAvatar";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import useAccountInfo from "@Hooks/useAccountInfo";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import LinearProgress from "@mui/material/LinearProgress";
import { useTranslation } from "react-i18next";
import { EDIT_SMALL_AVATAR_IMAGE_DATA_URI } from "../../Utils/assets";
import { hasAiAssistantAccess } from "@Utils/accountEntitlements";

type FormData = IUserData;

const ProfileSettings: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch<any>();

    const user = useSelector((state: RootStateType) => state.auth.user);
    const aiUsage = useSelector((state: RootStateType) => state.aiUsage.data);
    const [account] = useAccountInfo();

    const [userAvatar, setUserAvatar] = useState(
        user.avatar || { avatar: "", public_url: "" }
    );
    const [isCanUpdate, setIsCanUpdate] = useState(true);
    const [updateErromText, setUpdateErromText] = useState("");

    const { register, handleSubmit } = useForm<FormData>();

    const accountLimit = account?.event_limit ? `/${account?.event_limit}` : ""; //only for premium with limited events
    const warningThresholdPercentage = 80;
    const isPremiumAiAccount = hasAiAssistantAccess(account?.type);
    const accountTypeLabel = account?.type
        ? t(`pages.profile.account_types.${account.type}`)
        : "";
    const accountTypeChipSx = {
        backgroundColor: "#F2F0EB",
        color: "#888780",
        "& .MuiChip-label": {
            textTransform: "capitalize",
        },
    };

    if (account?.type === AccountTypes.Premium) {
        accountTypeChipSx.backgroundColor = "#EEEDFE";
        accountTypeChipSx.color = "#534AB7";
    } else if (account?.type === AccountTypes.PremiumAI) {
        accountTypeChipSx.backgroundColor = "#E1F5EE";
        accountTypeChipSx.color = "#0F6E56";
    }

    const saveImage = setUserAvatar;

    useEffect(() => {
        if (isPremiumAiAccount) {
            dispatch(AiUsageActions.fetchAiUsage());
        }
    }, [dispatch, isPremiumAiAccount]);

    const aiUsageData = aiUsage ?? {
        used_year: 0,
        remaining_year: 120000,
        total_year: 120000,
        used_month: 0,
        monthly_reference: 10000,
        contract_year_start: "",
        contract_year_end: "",
        current_month: "",
        limit_reached: false,
    };

    const annualPercentage = useMemo(() => {
        if (aiUsageData.total_year <= 0) return 0;
        return Math.min(
            (aiUsageData.used_year / aiUsageData.total_year) * 100,
            100
        );
    }, [aiUsageData]);

    const monthlyPercentage = useMemo(() => {
        if (aiUsageData.monthly_reference <= 0) return 0;
        return Math.min(
            (aiUsageData.used_month / aiUsageData.monthly_reference) * 100,
            100
        );
    }, [aiUsageData]);

    const isWarning =
        !!aiUsage &&
        !aiUsage.limit_reached &&
        annualPercentage >= warningThresholdPercentage;
    const isLimitReached = !!aiUsage?.limit_reached;

    const usageProgressColor = isLimitReached
        ? "error"
        : isWarning
        ? "warning"
        : "success";

    const formatNumber = (value: number) => value.toLocaleString();

    const formatContractYear = (startDate: string, endDate: string) => {
        const start = new Date(startDate);
        const end = new Date(endDate);

        const formatter = new Intl.DateTimeFormat("en-US", {
            month: "short",
            year: "numeric",
        });

        return `${formatter.format(start)} - ${formatter.format(end)}`;
    };

    const handleDialogImageUploadOpen = () => {
        dispatch(
            dialogActions.openDialog({
                id: DialogComponents.UploadChannelCoverDialog,
                props: {
                    user,
                    imageType: ImageType.UserProfile,
                    saveImage: saveImage,
                    tempImageData: userAvatar,
                },
            })
        );
    };

    const isEmptyObject = (obj: object) => Object.keys(obj).length === 0;

    const onSubmit = async (data: FormData) => {
        const userFormData = {
            email: data.email.trim(),
            username: data.username.trim().toLowerCase(),
            avatar: (userAvatar as { avatar?: string })?.avatar,
        };

        const dataToSend: Partial<typeof userFormData> = {};
        (Object.keys(userFormData) as Array<keyof typeof userFormData>).forEach(
            (prop) => {
                if (userFormData[prop] !== user[prop]) {
                    dataToSend[prop] = userFormData[prop];
                }
            }
        );

        if (!isEmptyObject(dataToSend)) {
            try {
                await AccountService.updateProfileSettings(dataToSend);
                setIsCanUpdate(true);
                setUpdateErromText("");

                dispatch(
                    AuthActions.updateUserData({ ...user, ...dataToSend })
                );

                dispatch(
                    NotificationActions.notification({
                        snackbarOpen: true,
                        snackbarType: "success",
                        snackbarMessage: t("snackbar_succes"),
                    })
                );
            } catch (error) {
                setIsCanUpdate(false);
                // @ts-ignore
                setUpdateErromText(error.message);
                dispatch(
                    NotificationActions.notification({
                        snackbarOpen: true,
                        snackbarType: "error",
                        snackbarMessage: t("snackbar_fail"),
                    })
                );
            }
        }
    };

    return (
        <Container>
            <StyledHeader>{t("pages.profile.title")}</StyledHeader>
            <Grid container spacing={6}>
                <Grid item xs={12}>
                    <Badge
                        overlap="circular"
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                        }}
                        badgeContent={
                            <Avatar
                                alt="profile image"
                                src={EDIT_SMALL_AVATAR_IMAGE_DATA_URI}
                                sx={{
                                    width: 48,
                                    height: 48,
                                    border: "0.1px solid lightgray",
                                    cursor: "pointer",
                                }}
                            />
                        }
                        onClick={handleDialogImageUploadOpen}
                    >
                        <UserAvatar
                            username={user.username}
                            avatar={userAvatar}
                            size="large"
                        />
                    </Badge>
                    {account?.type && (
                        <p>
                            <Chip
                                variant="filled"
                                size="medium"
                                label={accountTypeLabel}
                                sx={accountTypeChipSx}
                            />
                        </p>
                    )}
                </Grid>
                <Grid item xs={12}>
                    <List component="nav" aria-label="account-info">
                        <ListItem>
                            <ListItemIcon sx={{ minWidth: "auto", mr: "10px" }}>
                                <EmojiEventsIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary={t("pages.profile.events_created", {
                                    total_events_created:
                                        account?.total_events_created,
                                    accountLimit: accountLimit,
                                })}
                            />
                        </ListItem>
                    </List>
                    <form
                        id="user-dialog"
                        noValidate
                        onSubmit={handleSubmit(onSubmit)}
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                        }}
                    >
                        <Grid
                            container
                            spacing={4}
                            sx={{
                                p: "8px",
                                m: 0,
                                width: "100%",
                                position: "relative",
                            }}
                        >
                            {!isCanUpdate && (
                                <Grid
                                    item
                                    xs={12}
                                    sx={{
                                        position: "absolute",
                                        top: "-44px",
                                        width: "calc(100% - 16px)",
                                    }}
                                >
                                    <Alert severity="error" variant="filled">
                                        {updateErromText}
                                    </Alert>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <TextField
                                    defaultValue={user.username}
                                    autoComplete="username"
                                    name="username"
                                    variant="outlined"
                                    label={t("pages.profile.username")}
                                    required
                                    fullWidth
                                    inputRef={
                                        register("username", {
                                            required: String(
                                                t(
                                                    "pages.profile.enter_username"
                                                )
                                            ),
                                        }).ref
                                    }
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    defaultValue={user.email}
                                    autoComplete="email"
                                    name="email"
                                    variant="outlined"
                                    label={t("pages.profile.email_address")}
                                    required
                                    fullWidth
                                    inputRef={
                                        register("email", {
                                            required: true,
                                        }).ref
                                    }
                                />
                            </Grid>
                        </Grid>

                        <Box textAlign="center">
                            <Button
                                id="profile-settings-submit"
                                type="submit"
                                variant="contained"
                                color="primary"
                                sx={{
                                    backgroundColor: "#22C2FC",
                                    p: "10px 26px",
                                    borderRadius: "25px",
                                }}
                            >
                                {t("pages.profile.save")}
                            </Button>
                        </Box>
                    </form>
                </Grid>

                {isPremiumAiAccount && (
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                borderTop: "1px solid #ECECEC",
                                pt: 5,
                                mt: 1,
                                mb: 3,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 3,
                                    gap: 2,
                                    flexWrap: "wrap",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.2,
                                    }}
                                >
                                    <AutoAwesomeOutlinedIcon />
                                    <Box
                                        component="span"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: "1.35rem",
                                        }}
                                    >
                                        {t(
                                            "pages.profile.ai_assistant_usage.title"
                                        )}
                                    </Box>
                                </Box>

                                {aiUsageData.contract_year_start &&
                                    aiUsageData.contract_year_end && (
                                        <Chip
                                            label={formatContractYear(
                                                aiUsageData.contract_year_start,
                                                aiUsageData.contract_year_end
                                            )}
                                            sx={{
                                                backgroundColor: "#F2F0EB",
                                                fontSize: ".85rem",
                                            }}
                                        />
                                    )}
                            </Box>

                            {(isWarning || isLimitReached) && (
                                <Alert
                                    severity={
                                        isLimitReached ? "error" : "warning"
                                    }
                                    sx={{ mb: 2.5 }}
                                >
                                    {isLimitReached
                                        ? t(
                                              "pages.profile.ai_assistant_usage.limit_reached"
                                          )
                                        : t(
                                              "pages.profile.ai_assistant_usage.warning_80"
                                          )}
                                </Alert>
                            )}

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={5}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: "10px",
                                            backgroundColor: "#F2F0EB",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                fontSize: "1rem",
                                                color: "text.secondary",
                                            }}
                                        >
                                            {t(
                                                "pages.profile.ai_assistant_usage.this_month"
                                            )}
                                        </Box>
                                        <Box
                                            sx={{
                                                fontSize: "1.8rem",
                                                fontWeight: 700,
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {formatNumber(
                                                aiUsageData.used_month
                                            )}
                                        </Box>
                                        <Box
                                            sx={{
                                                fontSize: "1rem",
                                                color: "text.secondary",
                                            }}
                                        >
                                            of{" "}
                                            {formatNumber(
                                                aiUsageData.monthly_reference
                                            )}{" "}
                                            {t(
                                                "pages.profile.ai_assistant_usage.reference"
                                            )}
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={7}>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gap: 1.5,
                                            height: "100%",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                mb: 0.4,
                                                fontSize: "0.95rem",
                                            }}
                                        >
                                            <Box>
                                                {t(
                                                    "pages.profile.ai_assistant_usage.annual_usage"
                                                )}
                                            </Box>
                                            <Box>
                                                {t(
                                                    "pages.profile.ai_assistant_usage.percent_used",
                                                    {
                                                        percent:
                                                            Math.round(
                                                                annualPercentage
                                                            ),
                                                    }
                                                )}
                                            </Box>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={annualPercentage}
                                            color={usageProgressColor}
                                            sx={{
                                                height: 9,
                                                borderRadius: 6,
                                                backgroundColor: "#ECEAE5",
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                fontSize: "0.95rem",
                                            }}
                                        >
                                            <Box>
                                                {t(
                                                    "pages.profile.ai_assistant_usage.monthly_pace"
                                                )}
                                            </Box>
                                            <Box>
                                                {t(
                                                    "pages.profile.ai_assistant_usage.percent_of_reference",
                                                    {
                                                        percent:
                                                            Math.round(
                                                                monthlyPercentage
                                                            ),
                                                    }
                                                )}
                                            </Box>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={monthlyPercentage}
                                            color={usageProgressColor}
                                            sx={{
                                                height: 9,
                                                borderRadius: 6,
                                                backgroundColor: "#ECEAE5",
                                            }}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                )}
            </Grid>
        </Container>
    );
};

export default compose(withDefaultLayout)(ProfileSettings);
